import { HistoricalRedemption } from './redemption/historicalRedemption';


/**
 * Entitlement attributes from FTP which are stored as key-value pairs. This is just how RTP stores dynamic entitlements
 * information.
 */
export type EntitlementAttr = {
  name: string,
  value: string
};


// /**
//  * An attribute of an entitlement's fulfillment season (i.e. the season to which the entitlement applies)
//  */
// export type FulfillmentSeasonAttr = {
//   fulfillmentSeasonAttributes: EntitlementAttr[];
//   fulfillmentSeasonDescription: string;
//   fulfillmentSeasonId: number;
// };


/**
 * The intersection of QuickEntitlement and Entitlement
 */
export interface BaseEntitlement {
  masterGuestId: string;
  entitlementId: number;
  entitlementCategoryId: number;
  entitlementTypeId: number;

  //entitlementFulfillmentSeasonAttributes: FulfillmentSeasonAttr[];

  /**
   * The entitlement's status:
   * 1 - active
   * 2 - returned
   * 3 - expired
   * 4 - inactive
   */
  entitlementStatusId: number;

  /**
   * Indicates if the entitlement is restricted to a specific location.
   */
  isLocationRestricted: boolean | undefined;

  /**
   * Not from API: a calculated property we sometimes populate with the season prefixed to the entitlement name.
   */
  name?: string;
}


/**
 * Something an IKON guest is entitled to e.g. special season passes at non-Alterra resorts, or an IKON Base Pass.
 * This is a quick entitlement because it's a messed-up model that comes from a really fast API endpoint used by the
 * gate system. Entitlement is the correct entitlement model from the data store. This model is a maverik inventing its
 * own property naming used nowhere else.
 */
export interface QuickEntitlement extends BaseEntitlement {
  expirationDate: string;
  effectiveDate: string;
  externalId: string;
  entitlementTypeAttributes: EntitlementAttr[];
  prepopulateGuest: boolean;
}


/**
 * Entitlements are something the guest is entitled to. This could be any benefit, but most notably in the case of this
 * app, an entitlement is often a season pass (the abstract concept of one, not the actual card (the card is a Media)).
 */
export interface Entitlement extends BaseEntitlement {
  isValid: boolean;
  invalidDescription: string;
  entitlementDescription: string;
  entitlementTypeId: number;
  entitlementAttributes: EntitlementAttr[];
  resortAbbrev: string;
  maxAllowed: number;
  used: number;
  available: number;
  redemptionHistory: HistoricalRedemption[];
  
  expirationDate?: string | Date;
  effectiveDate?: string | Date;
}

/**
 * Information about whether a specific entitlement is hotlisted (blacklisted), and details about that
 *  hotlisting if applicable.
 */
export interface EntitlementHotlist {
  entitlementId:          number;
  entitlementHotlistId:   number;
  status:                 string;
  resortAbbreviation?:    string;
  isHotlistGlobal:        boolean;
  hotlistComment?:        string;
  unhotlistComment?:      string;
  hotlistTypeId:          number;
  effectiveDate?:         string | Date;
  expirationDate?:        string | Date;
  createdDate?:           string | Date;
  modifiedDate?:          string | Date;
  modifiedById?:          string;
  createdById?:           string;

  /**
   * Human-readable title derived from isHotlistGlobal and resort attributes
   */
  title?:                 string;
}

/**
 * Returned from entitlementshares endpoint. Represents an entitlement that has been shared (ie friends and family)
 */
export interface EntitlementShare {
  guestId: string;
  externalShareId: string,
  fromEntitlementId: number,
  toEntitlementId: number,
  fromMasterGuestId: string,
  toMasterGuestId: string,
  toEmailAddress: string,
  shareUtcDateTime: string,
  claimedUtcDateTime: string,
  revokedUtcDateTime: string,
  entitlementShareStatusId: number,
  entitlementShareStatusDescription: string;
}


/**
 * A summary of redemptions and entitlement shares for display in the Friends and Family modal.
 */
export interface FnFRedemptionSummary {
  redemptionId: string;
  externalShareId: string;
  redeemDate: string;
  redeemResort: string;
  redeemQuantity: number;
  refId: string;
  summaryType: string;
  summaryTypeDescription: string;
}

/**
 * The signature status of a waiver object is a numerical code what that maps like this enum.
 */
export enum ENTITLEMENT_SHARE_STATUS {
  SHARED   = 1,
  CLAIMED  = 2,
  REVOKED  = 3,
}

